import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Handshake, Building, Users, Globe, Star, CheckCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const Partners = () => {
  const { i18n } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    partnerType: '',
    contribution: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const content = {
    fr: {
      title: "Devenez Partenaire",
      subtitle: "Rejoignez-nous dans cette aventure pour le développement durable en Haïti",
      intro: "En devenant partenaire du Marathon de l'Avenir EDENLIFE 2026, vous associez votre image à une cause noble et bénéficiez d'une visibilité exceptionnelle auprès de notre communauté.",
      benefitsTitle: "Avantages Partenaires",
      benefits: [
        { icon: Globe, title: "Visibilité Internationale", desc: "Votre logo sur notre site, bannières et communications" },
        { icon: Users, title: "Accès Communauté", desc: "Connexion avec notre réseau de supporters engagés" },
        { icon: Star, title: "Image Positive", desc: "Association à une cause humanitaire reconnue" },
        { icon: Building, title: "Événements VIP", desc: "Invitation au tirage au sort et événements exclusifs" }
      ],
      typesTitle: "Types de Partenariat",
      types: [
        { value: "platinum", label: "Platine (10 000 USD+)", desc: "Logo principal, mentions dans toutes communications" },
        { value: "gold", label: "Or (5 000 USD+)", desc: "Logo visible, mention sur bannières" },
        { value: "silver", label: "Argent (2 500 USD+)", desc: "Logo sur site web et réseaux sociaux" },
        { value: "bronze", label: "Bronze (1 000 USD+)", desc: "Mention sur la page partenaires" },
        { value: "media", label: "Partenaire Média", desc: "Échange de visibilité et promotion" },
        { value: "inkind", label: "Partenaire en Nature", desc: "Contribution de produits ou services" }
      ],
      formTitle: "Formulaire de Contact Partenariat",
      fields: {
        companyName: "Nom de l'entreprise / Organisation",
        contactName: "Nom du contact",
        email: "Email professionnel",
        phone: "Téléphone",
        website: "Site web (optionnel)",
        partnerType: "Type de partenariat souhaité",
        contribution: "Contribution envisagée",
        message: "Message / Proposition"
      },
      placeholders: {
        companyName: "Votre entreprise",
        contactName: "Jean Dupont",
        email: "contact@entreprise.com",
        phone: "+1 xxx-xxx-xxxx",
        website: "https://www.votresite.com",
        contribution: "Ex: 5000 USD, services marketing, etc.",
        message: "Décrivez votre proposition de partenariat..."
      },
      selectType: "Sélectionnez un type de partenariat",
      submitBtn: "Envoyer ma demande",
      sending: "Envoi en cours...",
      successMessage: "Demande envoyée avec succès ! Notre équipe vous contactera sous 48h.",
      contactNote: "Pour toute question, contactez-nous directement :",
      errorMessage: "Veuillez remplir les champs obligatoires"
    },
    en: {
      title: "Become a Partner",
      subtitle: "Join us in this adventure for sustainable development in Haiti",
      intro: "By becoming a partner of the EDENLIFE Marathon 2026, you associate your image with a noble cause and benefit from exceptional visibility among our community.",
      benefitsTitle: "Partner Benefits",
      benefits: [
        { icon: Globe, title: "International Visibility", desc: "Your logo on our website, banners and communications" },
        { icon: Users, title: "Community Access", desc: "Connection with our network of engaged supporters" },
        { icon: Star, title: "Positive Image", desc: "Association with a recognized humanitarian cause" },
        { icon: Building, title: "VIP Events", desc: "Invitation to draw and exclusive events" }
      ],
      typesTitle: "Partnership Types",
      types: [
        { value: "platinum", label: "Platinum ($10,000+)", desc: "Main logo, mentions in all communications" },
        { value: "gold", label: "Gold ($5,000+)", desc: "Visible logo, mention on banners" },
        { value: "silver", label: "Silver ($2,500+)", desc: "Logo on website and social media" },
        { value: "bronze", label: "Bronze ($1,000+)", desc: "Mention on partners page" },
        { value: "media", label: "Media Partner", desc: "Exchange of visibility and promotion" },
        { value: "inkind", label: "In-Kind Partner", desc: "Contribution of products or services" }
      ],
      formTitle: "Partnership Contact Form",
      fields: {
        companyName: "Company / Organization Name",
        contactName: "Contact Name",
        email: "Professional Email",
        phone: "Phone",
        website: "Website (optional)",
        partnerType: "Desired Partnership Type",
        contribution: "Planned Contribution",
        message: "Message / Proposal"
      },
      placeholders: {
        companyName: "Your company",
        contactName: "John Doe",
        email: "contact@company.com",
        phone: "+1 xxx-xxx-xxxx",
        website: "https://www.yoursite.com",
        contribution: "Ex: $5000, marketing services, etc.",
        message: "Describe your partnership proposal..."
      },
      selectType: "Select a partnership type",
      submitBtn: "Submit my request",
      sending: "Sending...",
      successMessage: "Request sent successfully! Our team will contact you within 48h.",
      contactNote: "For any questions, contact us directly:",
      errorMessage: "Please fill in required fields"
    },
    ht: {
      title: "Vin Patnè",
      subtitle: "Rejwenn nou nan avanti sa a pou devlopman dirab an Ayiti",
      intro: "Lè w vin patnè Maraton EDENLIFE Entènasyonal 2026 la, ou asosye imaj ou ak yon kòz nòb epi benefisye de vizibilite eksepsyonèl nan kominote nou an.",
      benefitsTitle: "Avantaj Patnè",
      benefits: [
        { icon: Globe, title: "Vizibilite Entènasyonal", desc: "Logo ou sou sit nou an, banyè ak kominikasyon" },
        { icon: Users, title: "Aksè Kominote", desc: "Koneksyon ak rezo sipòtè angaje nou yo" },
        { icon: Star, title: "Imaj Pozitif", desc: "Asosyasyon ak yon kòz imanitè rekonèt" },
        { icon: Building, title: "Evènman VIP", desc: "Envitasyon pou tiraj ak evènman eksklizif" }
      ],
      typesTitle: "Tip Patenarya",
      types: [
        { value: "platinum", label: "Platin (10 000 USD+)", desc: "Logo prensipal, mansyon nan tout kominikasyon" },
        { value: "gold", label: "Lò (5 000 USD+)", desc: "Logo vizib, mansyon sou banyè" },
        { value: "silver", label: "Ajan (2 500 USD+)", desc: "Logo sou sit entènèt ak rezo sosyal" },
        { value: "bronze", label: "Bwonz (1 000 USD+)", desc: "Mansyon sou paj patnè" },
        { value: "media", label: "Patnè Medya", desc: "Echanj vizibilite ak pwomosyon" },
        { value: "inkind", label: "Patnè an Nati", desc: "Kontribisyon pwodui oswa sèvis" }
      ],
      formTitle: "Fòmilè Kontak Patenarya",
      fields: {
        companyName: "Non Antrepriz / Òganizasyon",
        contactName: "Non Kontak",
        email: "Imel Pwofesyonèl",
        phone: "Telefòn",
        website: "Sit entènèt (opsyonèl)",
        partnerType: "Tip Patenarya Dezire",
        contribution: "Kontribisyon Planifye",
        message: "Mesaj / Pwopozisyon"
      },
      placeholders: {
        companyName: "Antrepriz ou",
        contactName: "Jan Batis",
        email: "kontak@antrepriz.com",
        phone: "+1 xxx-xxx-xxxx",
        website: "https://www.sitou.com",
        contribution: "Eg: 5000 USD, sèvis maketing, elatriye.",
        message: "Dekri pwopozisyon patenarya ou..."
      },
      selectType: "Chwazi yon tip patenarya",
      submitBtn: "Voye demann mwen",
      sending: "Ap voye...",
      successMessage: "Demann voye avèk siksè! Ekip nou ap kontakte ou nan 48 èdtan.",
      contactNote: "Pou nenpòt kesyon, kontakte nou dirèkteman:",
      errorMessage: "Tanpri ranpli chan obligatwa yo"
    },
    es: {
      title: "Conviértase en Socio",
      subtitle: "Únase a nosotros en esta aventura por el desarrollo sostenible en Haití",
      intro: "Al convertirse en socio del Maratón EDENLIFE 2026, asocia su imagen a una causa noble y se beneficia de una visibilidad excepcional entre nuestra comunidad.",
      benefitsTitle: "Beneficios para Socios",
      benefits: [
        { icon: Globe, title: "Visibilidad Internacional", desc: "Su logo en nuestro sitio, banners y comunicaciones" },
        { icon: Users, title: "Acceso a la Comunidad", desc: "Conexión con nuestra red de simpatizantes comprometidos" },
        { icon: Star, title: "Imagen Positiva", desc: "Asociación con una causa humanitaria reconocida" },
        { icon: Building, title: "Eventos VIP", desc: "Invitación al sorteo y eventos exclusivos" }
      ],
      typesTitle: "Tipos de Asociación",
      types: [
        { value: "platinum", label: "Platino ($10,000+)", desc: "Logo principal, mención en todas las comunicaciones" },
        { value: "gold", label: "Oro ($5,000+)", desc: "Logo visible, mención en banners" },
        { value: "silver", label: "Plata ($2,500+)", desc: "Logo en sitio web y redes sociales" },
        { value: "bronze", label: "Bronce ($1,000+)", desc: "Mención en página de socios" },
        { value: "media", label: "Socio de Medios", desc: "Intercambio de visibilidad y promoción" },
        { value: "inkind", label: "Socio en Especie", desc: "Contribución de productos o servicios" }
      ],
      formTitle: "Formulario de Contacto de Asociación",
      fields: {
        companyName: "Nombre de Empresa / Organización",
        contactName: "Nombre del Contacto",
        email: "Correo Profesional",
        phone: "Teléfono",
        website: "Sitio web (opcional)",
        partnerType: "Tipo de Asociación Deseada",
        contribution: "Contribución Planificada",
        message: "Mensaje / Propuesta"
      },
      placeholders: {
        companyName: "Su empresa",
        contactName: "Juan Pérez",
        email: "contacto@empresa.com",
        phone: "+1 xxx-xxx-xxxx",
        website: "https://www.susitio.com",
        contribution: "Ej: $5000, servicios de marketing, etc.",
        message: "Describa su propuesta de asociación..."
      },
      selectType: "Seleccione un tipo de asociación",
      submitBtn: "Enviar mi solicitud",
      sending: "Enviando...",
      successMessage: "¡Solicitud enviada con éxito! Nuestro equipo le contactará en 48h.",
      contactNote: "Para cualquier pregunta, contáctenos directamente:",
      errorMessage: "Por favor complete los campos obligatorios"
    },
    pt: {
      title: "Torne-se Parceiro",
      subtitle: "Junte-se a nós nesta aventura pelo desenvolvimento sustentável no Haiti",
      intro: "Ao se tornar parceiro da Maratona EDENLIFE 2026, você associa sua imagem a uma causa nobre e se beneficia de uma visibilidade excepcional em nossa comunidade.",
      benefitsTitle: "Benefícios para Parceiros",
      benefits: [
        { icon: Globe, title: "Visibilidade Internacional", desc: "Seu logo em nosso site, banners e comunicações" },
        { icon: Users, title: "Acesso à Comunidade", desc: "Conexão com nossa rede de apoiadores engajados" },
        { icon: Star, title: "Imagem Positiva", desc: "Associação com uma causa humanitária reconhecida" },
        { icon: Building, title: "Eventos VIP", desc: "Convite para sorteio e eventos exclusivos" }
      ],
      typesTitle: "Tipos de Parceria",
      types: [
        { value: "platinum", label: "Platina (R$10.000+)", desc: "Logo principal, menção em todas as comunicações" },
        { value: "gold", label: "Ouro (R$5.000+)", desc: "Logo visível, menção em banners" },
        { value: "silver", label: "Prata (R$2.500+)", desc: "Logo no site e redes sociais" },
        { value: "bronze", label: "Bronze (R$1.000+)", desc: "Menção na página de parceiros" },
        { value: "media", label: "Parceiro de Mídia", desc: "Troca de visibilidade e promoção" },
        { value: "inkind", label: "Parceiro em Espécie", desc: "Contribuição de produtos ou serviços" }
      ],
      formTitle: "Formulário de Contato de Parceria",
      fields: {
        companyName: "Nome da Empresa / Organização",
        contactName: "Nome do Contato",
        email: "Email Profissional",
        phone: "Telefone",
        website: "Site (opcional)",
        partnerType: "Tipo de Parceria Desejada",
        contribution: "Contribuição Planejada",
        message: "Mensagem / Proposta"
      },
      placeholders: {
        companyName: "Sua empresa",
        contactName: "João Silva",
        email: "contato@empresa.com",
        phone: "+55 xxx-xxx-xxxx",
        website: "https://www.seusite.com",
        contribution: "Ex: R$5000, serviços de marketing, etc.",
        message: "Descreva sua proposta de parceria..."
      },
      selectType: "Selecione um tipo de parceria",
      submitBtn: "Enviar minha solicitação",
      sending: "Enviando...",
      successMessage: "Solicitação enviada com sucesso! Nossa equipe entrará em contato em 48h.",
      contactNote: "Para qualquer dúvida, entre em contato diretamente:",
      errorMessage: "Por favor preencha os campos obrigatórios"
    }
  };

  const t = content[i18n.language] || content.fr;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.companyName || !formData.contactName || !formData.email || !formData.partnerType) {
      toast.error(t.errorMessage);
      return;
    }
    
    setSending(true);
    // Simulate sending
    setTimeout(() => {
      toast.success(t.successMessage);
      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        website: '',
        partnerType: '',
        contribution: '',
        message: ''
      });
      setSending(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen pt-24 px-6 pb-12" style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}>
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="text-center mb-12">
            <Handshake className="w-20 h-20 text-secondary mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.title}
            </h1>
            <p className="text-lg text-accent font-semibold mb-4">{t.subtitle}</p>
            <p className="text-gray-600 max-w-3xl mx-auto">{t.intro}</p>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {t.benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/90 backdrop-blur-md rounded-2xl p-6 text-center shadow-lg"
                >
                  <div className="w-14 h-14 mx-auto mb-4 bg-secondary/10 rounded-full flex items-center justify-center">
                    <Icon className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="font-bold text-primary mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Partnership Types */}
            <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8">
              <h2 className="text-2xl font-bold text-primary mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t.typesTitle}
              </h2>
              <div className="space-y-4">
                {t.types.map((type, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      formData.partnerType === type.value
                        ? 'border-secondary bg-secondary/10'
                        : 'border-gray-200 hover:border-secondary/50'
                    }`}
                    onClick={() => setFormData({ ...formData, partnerType: type.value })}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-bold text-primary">{type.label}</h4>
                        <p className="text-sm text-gray-600">{type.desc}</p>
                      </div>
                      {formData.partnerType === type.value && (
                        <CheckCircle className="w-6 h-6 text-secondary" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8">
              <h2 className="text-2xl font-bold text-primary mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t.formTitle}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="companyName">{t.fields.companyName} *</Label>
                  <Input
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="mt-1"
                    placeholder={t.placeholders.companyName}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contactName">{t.fields.contactName} *</Label>
                    <Input
                      id="contactName"
                      value={formData.contactName}
                      onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                      className="mt-1"
                      placeholder={t.placeholders.contactName}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t.fields.phone}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1"
                      placeholder={t.placeholders.phone}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">{t.fields.email} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1"
                    placeholder={t.placeholders.email}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="website">{t.fields.website}</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="mt-1"
                    placeholder={t.placeholders.website}
                  />
                </div>

                <div>
                  <Label htmlFor="contribution">{t.fields.contribution}</Label>
                  <Input
                    id="contribution"
                    value={formData.contribution}
                    onChange={(e) => setFormData({ ...formData, contribution: e.target.value })}
                    className="mt-1"
                    placeholder={t.placeholders.contribution}
                  />
                </div>

                <div>
                  <Label htmlFor="message">{t.fields.message}</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="mt-1 min-h-[100px]"
                    placeholder={t.placeholders.message}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 rounded-full text-lg font-semibold"
                >
                  <Send className="w-5 h-5 mr-2" />
                  {sending ? t.sending : t.submitBtn}
                </Button>
              </form>

              <div className="mt-6 p-4 bg-primary/5 rounded-xl text-center">
                <p className="text-sm text-gray-600 mb-2">{t.contactNote}</p>
                <p className="font-semibold text-primary">info@marathon-edenlifeintl.org</p>
                <p className="text-primary">1 866-722-7007</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Partners;
